import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Calendar, CircleAlert, Info, LucideAngularModule, Pencil, Split, X } from 'lucide-angular';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProductVariantOptionService } from '@app/products/shared/product-variant-option.service';
import { ProductVariantModel } from '@app/products/shared/product-variant.model';
import { ProductVariantOptionModel } from '@app/products/shared/product-variant-option.model';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { CurrencyPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { ToastService } from '@app/shared/toast/toast.service';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';

type UsageDetails = {
  events: Array<{ id: number; name: string; reference?: string | null; date: string | null }>;
  event_count: number;
};

@Component({
  selector: 'app-product-variant-options',
  templateUrl: './product-variant-options.component.html',
  imports: [LucideAngularModule, FormsModule, ReactiveFormsModule, TextInputComponent, DatePickerComponent, CurrencyPipe, NgClass, NgTemplateOutlet, ModalComponent],
})
export class ProductVariantOptionsComponent implements OnChanges {
  @Input('item') variant?: ProductVariantModel;
  @Input() activeVariantId: number | null = null;
  @Output() formOpen = new EventEmitter<number>();
  @Output() formClose = new EventEmitter<void>();

  protected error?: string;
  protected editingOptionId: number | null = null;
  protected optionUsageDetails?: UsageDetails;
  protected optionUsageOption?: ProductVariantOptionModel;
  protected optionUsageContext: 'delete' | 'view' = 'delete';
  protected formMode: 'add' | 'edit' | null = null;
  protected tierPricingOption?: ProductVariantOptionModel;
  protected tierError?: string;

  protected optionForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl<string | null>(null, { validators: [Validators.maxLength(255)] }),
    price: new FormControl<number | null>(null),
    category: new FormControl<'Full Price' | 'Complimentary' | 'Half Price'>('Full Price', { nonNullable: true }),
    counts_toward_minimum_spend: new FormControl<boolean>(true, { nonNullable: true }),
    max_quantity: new FormControl<number | null>(null, { validators: [Validators.min(0)] }),
    info_message: new FormControl<string | null>(null, { validators: [Validators.maxLength(255)] }),
    available_from: new FormControl<string>(''),
    available_until: new FormControl<string>(''),
  });

  protected tierForm = new FormGroup({
    tier_complimentary_option_id: new FormControl<number | null>(null, { validators: [Validators.required] }),
    tier_half_option_id: new FormControl<number | null>(null, { validators: [Validators.required] }),
    tier_full_limit: new FormControl<number | null>(null, { validators: [Validators.required, Validators.min(0)] }),
    tier_complimentary_limit: new FormControl<number | null>(null, { validators: [Validators.required, Validators.min(0)] }),
  });

  protected nameControl = this.optionForm.controls.name;
  protected priceControl = this.optionForm.controls.price;
  protected categoryControl = this.optionForm.controls.category;
  protected countsControl = this.optionForm.controls.counts_toward_minimum_spend;

  protected readonly categoryChoices: Array<'Full Price' | 'Complimentary' | 'Half Price'> = [
    'Full Price',
    'Complimentary',
    'Half Price',
  ];

  constructor(
    private service: ProductVariantOptionService,
    private router: Router,
    private toastService: ToastService,
    private confirmationModal: ConfirmationModalService,
  ) {}

  protected readonly XIcon = X;
  protected readonly CalendarIcon = Calendar;
  protected readonly PencilIcon = Pencil;
  protected readonly InfoIcon = Info;
  protected readonly TierIcon = Split;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['activeVariantId'] && this.activeVariantId !== this.variant?.id) {
      this.resetFormState(false);
    }

    if (changes['variant'] && this.hasTierModal) {
      this.syncTierControlAvailability();
    }
  }

  protected get isFormVisible(): boolean {
    return this.activeVariantId === this.variant?.id && this.formMode !== null;
  }

  protected get formLabel(): string {
    return this.formMode === 'edit' ? 'Edit Option' : 'Add Option';
  }

  protected get editingOption(): ProductVariantOptionModel | undefined {
    if (!this.variant?.options || this.editingOptionId === null) {
      return undefined;
    }

    return this.variant.options.find((option) => option.id === this.editingOptionId);
  }

  protected get sortedOptions(): ProductVariantOptionModel[] {
    return [...(this.variant?.options || [])].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
  }

  protected get tierComplimentaryOptions(): ProductVariantOptionModel[] {
    return (this.variant?.options || []).filter((option) => option.category === 'Complimentary');
  }

  protected get tierHalfOptions(): ProductVariantOptionModel[] {
    return (this.variant?.options || []).filter((option) => option.category === 'Half Price');
  }

  protected get canSelectTierOptions(): boolean {
    return this.tierComplimentaryOptions.length > 0 && this.tierHalfOptions.length > 0;
  }

  protected getTierDisabledReason(option: ProductVariantOptionModel): string | null {
    if (option.category && option.category !== 'Full Price') {
      return 'Tiered pricing is only available for Full Price options.';
    }

    if (this.tierComplimentaryOptions.length === 0) {
      return 'Create a Complimentary option to enable tiered pricing.';
    }

    if (this.tierHalfOptions.length === 0) {
      return 'Create a Half Price option to enable tiered pricing.';
    }

    return null;
  }

  protected hasTierConfig(option: ProductVariantOptionModel): boolean {
    return (
      option.tier_full_limit !== null &&
      option.tier_full_limit !== undefined &&
      option.tier_complimentary_limit !== null &&
      option.tier_complimentary_limit !== undefined &&
      !!option.tier_complimentary_option_id &&
      !!option.tier_half_option_id
    );
  }

  protected getTierOptionName(optionId: number | null | undefined): string {
    if (!optionId || !this.variant?.options) {
      return '—';
    }

    return this.variant.options.find((option) => option.id === optionId)?.name || '—';
  }

  protected get hasTierModal(): boolean {
    return !!this.tierPricingOption;
  }

  protected get tierFlowText(): string {
    const fullLimit = this.toOptionalInt(this.tierForm.controls.tier_full_limit.value);
    const complimentaryLimit = this.toOptionalInt(this.tierForm.controls.tier_complimentary_limit.value);

    if (fullLimit !== null && complimentaryLimit !== null) {
      return `Full for first ${fullLimit}, Complimentary for next ${complimentaryLimit}, Half for the rest.`;
    }

    return 'Full for first quantity, Complimentary for next quantity, Half for the rest.';
  }

  protected get tierPreviewText(): string | null {
    const fullLimit = this.toOptionalInt(this.tierForm.controls.tier_full_limit.value);
    const complimentaryLimit = this.toOptionalInt(this.tierForm.controls.tier_complimentary_limit.value);

    if (fullLimit === null || complimentaryLimit === null || !this.tierPricingOption) {
      return null;
    }

    const exampleRemainder = 10;
    const exampleTotal = fullLimit + complimentaryLimit + exampleRemainder;
    const fullPrice = this.formatTierPrice(this.tierPricingOption.price);
    const complimentaryPrice = this.formatTierPrice(
      this.getTierOptionPrice(this.tierForm.controls.tier_complimentary_option_id.value)
    );
    const halfPrice = this.formatTierPrice(
      this.getTierOptionPrice(this.tierForm.controls.tier_half_option_id.value)
    );

    return `Example: ${exampleTotal} items -> Full ${fullLimit} @ ${fullPrice}, Complimentary ${complimentaryLimit} @ ${complimentaryPrice}, Half ${exampleRemainder} @ ${halfPrice}.`;
  }

  remove(option: ProductVariantOptionModel) {
    if (!option.id) {
      return;
    }

    this.service.delete(option.id.toString()).subscribe({
      next: () => {
        if (this.variant) {
          this.variant.options = this.variant.options.filter((item) => item.id !== option.id);
        }
        this.optionUsageDetails = undefined;
        this.optionUsageOption = undefined;
        this.optionUsageContext = 'delete';
      },
      error: (res) => {
        if (res.status === 400 && res.error?.events) {
          this.optionUsageDetails = {
            events: res.error.events,
            event_count: res.error.event_count || res.error.events.length,
          };
          this.optionUsageContext = 'delete';
          this.optionUsageOption = option;
        } else {
          this.error = res.error.message;
          this.optionUsageDetails = undefined;
          this.optionUsageOption = undefined;
          this.optionUsageContext = 'delete';
        }
      },
    });
  }

  openOptionUsage(option: ProductVariantOptionModel, $event?: Event) {
    if ($event) {
      $event.stopPropagation();
    }
    if (!option.id) {
      return;
    }

    this.optionUsageContext = 'view';
    this.optionUsageOption = option;

    this.service.getUsage(option.id).subscribe({
      next: (response) => {
        this.optionUsageDetails = response;
      },
      error: () => {
        this.toastService.error('Failed to load option usage');
        this.closeOptionUsageModal();
      },
    });
  }

  closeOptionUsageModal() {
    this.optionUsageDetails = undefined;
    this.optionUsageOption = undefined;
    this.optionUsageContext = 'delete';
  }

  startAddOption($event?: Event) {
    if ($event) {
      $event.stopPropagation();
    }
    if (!this.variant?.id) {
      return;
    }
    this.formMode = 'add';
    this.editingOptionId = null;
    this.error = undefined;
    this.optionForm.reset({
      name: '',
      description: null,
      price: null,
      category: 'Full Price',
      counts_toward_minimum_spend: true,
      max_quantity: null,
      info_message: null,
      available_from: '',
      available_until: '',
    });
    this.emitFormOpen();
  }

  private emitFormOpen() {
    if (this.variant?.id) {
      this.formOpen.emit(this.variant.id);
    }
  }

  private resetFormState(emitClose = true) {
    this.formMode = null;
    this.editingOptionId = null;
    this.error = undefined;
    this.optionForm.reset({
      name: '',
      description: null,
      price: null,
      category: 'Full Price',
      counts_toward_minimum_spend: true,
      max_quantity: null,
      info_message: null,
      available_from: '',
      available_until: '',
    });
    if (emitClose) {
      this.formClose.emit();
    }
  }

  addOption($event?: Event) {
    if ($event) {
      $event.stopPropagation();
    }
    if (this.optionForm.invalid) {
      this.optionForm.markAllAsTouched();
      return;
    }
    if (!this.variant?.id) {
      return;
    }

    const priceValue = this.optionForm.get('price')?.value;
    const parsedPrice = priceValue ? parseFloat(priceValue.toString().replace(/,/g, '')) : null;

    this.service
      .save({
        name: this.optionForm.get('name')?.value!,
        description: this.optionForm.get('description')?.value ?? null,
        product_variant_id: this.variant.id,
        price: parsedPrice,
        category: this.optionForm.get('category')?.value,
        counts_toward_minimum_spend: this.optionForm.get('counts_toward_minimum_spend')?.value,
        max_quantity: this.optionForm.get('max_quantity')?.value ?? null,
        info_message: this.optionForm.get('info_message')?.value ?? null,
        available_from: this.optionForm.get('available_from')?.value || null,
        available_until: this.optionForm.get('available_until')?.value || null,
      } as any)
      .subscribe({
        next: (option) => {
          if (this.variant) {
            this.variant.options.push(option.data);
          }
          this.toastService.success('Option added');
          this.resetFormState();
        },
        error: (res) => {
          this.error = res.error.message;
          this.toastService.error(this.error || 'Failed to add option');
        },
      });
  }

  editOption(option: ProductVariantOptionModel, $event?: Event) {
    if ($event) {
      $event.stopPropagation();
    }
    this.editingOptionId = option.id;
    this.formMode = 'edit';
    this.optionForm.patchValue({
      name: option.name,
      description: option.description ?? null,
      price: option.price,
      category: option.category || 'Full Price',
      counts_toward_minimum_spend: option.counts_toward_minimum_spend ?? true,
      max_quantity: (option as any)['max_quantity'] ?? null,
      info_message: option.info_message ?? null,
      available_from: option.available_from ?? '',
      available_until: option.available_until ?? '',
    });
    this.error = undefined;
    this.emitFormOpen();
  }

  openTierPricing(option: ProductVariantOptionModel, $event?: Event) {
    if ($event) {
      $event.stopPropagation();
    }
    if (!option.id || this.getTierDisabledReason(option)) {
      return;
    }

    this.tierPricingOption = option;
    this.tierError = undefined;
    this.tierForm.reset({
      tier_complimentary_option_id: option.tier_complimentary_option_id ?? null,
      tier_half_option_id: option.tier_half_option_id ?? null,
      tier_full_limit: option.tier_full_limit ?? null,
      tier_complimentary_limit: option.tier_complimentary_limit ?? null,
    });
    this.syncTierControlAvailability();
  }

  closeTierPricing() {
    this.tierPricingOption = undefined;
    this.tierError = undefined;
    this.tierForm.reset({
      tier_complimentary_option_id: null,
      tier_half_option_id: null,
      tier_full_limit: null,
      tier_complimentary_limit: null,
    });
    this.syncTierControlAvailability();
  }

  saveTierPricing() {
    if (!this.tierPricingOption?.id || !this.variant?.id) {
      return;
    }

    if (this.tierForm.invalid) {
      this.tierForm.markAllAsTouched();
      return;
    }

    if (!this.canSelectTierOptions) {
      return;
    }

    const payload = this.buildOptionPayload(this.tierPricingOption, {
      tier_complimentary_option_id: this.toOptionalInt(this.tierForm.controls.tier_complimentary_option_id.value),
      tier_half_option_id: this.toOptionalInt(this.tierForm.controls.tier_half_option_id.value),
      tier_full_limit: this.toOptionalInt(this.tierForm.controls.tier_full_limit.value),
      tier_complimentary_limit: this.toOptionalInt(this.tierForm.controls.tier_complimentary_limit.value),
    });

    this.service.update(this.tierPricingOption.id, payload as any).subscribe({
      next: (response) => {
        if (this.variant) {
          const index = this.variant.options.findIndex((o) => o.id === this.tierPricingOption?.id);
          if (index !== -1) {
            this.variant.options[index] = response.data;
          }
        }
        this.toastService.success('Tiered pricing saved');
        this.closeTierPricing();
      },
      error: (res) => {
        this.tierError = res.error?.message || 'Failed to save tiered pricing';
        this.toastService.error(this.tierError || 'Failed to save tiered pricing');
      },
    });
  }

  async resetTierPricing() {
    if (!this.tierPricingOption?.id || !this.variant?.id) {
      return;
    }

    const confirmed = await this.confirmationModal.confirm({
      title: 'Reset tiered pricing',
      message: 'Reset tiered pricing for this option? This will clear all tier settings.',
      confirmLabel: 'Reset Pricing',
      cancelLabel: 'Keep Settings',
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) {
      return;
    }

    const payload = this.buildOptionPayload(this.tierPricingOption, {
      tier_complimentary_option_id: null,
      tier_half_option_id: null,
      tier_full_limit: null,
      tier_complimentary_limit: null,
    });

    this.service.update(this.tierPricingOption.id, payload as any).subscribe({
      next: (response) => {
        if (this.variant) {
          const index = this.variant.options.findIndex((o) => o.id === this.tierPricingOption?.id);
          if (index !== -1) {
            this.variant.options[index] = response.data;
          }
        }
        this.toastService.success('Tiered pricing reset');
        this.closeTierPricing();
      },
      error: (res) => {
        this.tierError = res.error?.message || 'Failed to reset tiered pricing';
        this.toastService.error(this.tierError || 'Failed to reset tiered pricing');
      },
    });
  }

  updateOption($event?: Event) {
    if ($event) {
      $event.stopPropagation();
    }
    if (this.optionForm.invalid || this.editingOptionId === null) {
      this.optionForm.markAllAsTouched();
      return;
    }
    if (!this.variant?.id) {
      return;
    }

    const priceValue = this.optionForm.get('price')?.value;
    const parsedPrice = priceValue ? parseFloat(priceValue.toString().replace(/,/g, '')) : null;

    this.service
      .update(this.editingOptionId, {
        name: this.optionForm.get('name')?.value!,
        description: this.optionForm.get('description')?.value ?? null,
        product_variant_id: this.variant.id,
        price: parsedPrice,
        category: this.optionForm.get('category')?.value,
        counts_toward_minimum_spend: this.optionForm.get('counts_toward_minimum_spend')?.value,
        max_quantity: this.optionForm.get('max_quantity')?.value ?? null,
        info_message: this.optionForm.get('info_message')?.value ?? null,
        available_from: this.optionForm.get('available_from')?.value || null,
        available_until: this.optionForm.get('available_until')?.value || null,
      } as any)
      .subscribe({
        next: (response) => {
          if (this.variant) {
            const index = this.variant.options.findIndex((o) => o.id === this.editingOptionId);
            if (index !== -1) {
              this.variant.options[index] = response.data;
            }
          }
          this.toastService.success('Option updated');
          this.resetFormState();
        },
        error: (res) => {
          this.error = res.error.message;
          this.toastService.error(this.error || 'Failed to update option');
        },
      });
  }

  cancelForm() {
    this.resetFormState();
  }

  protected readonly CircleAlert = CircleAlert;

  openPricingCalendar(optionId: number | null) {
    if (!optionId) return;
    this.router.navigate(['/product-variant-options', optionId, 'prices', 'calendar']);
  }

  private buildOptionPayload(option: ProductVariantOptionModel, overrides: Partial<ProductVariantOptionModel>) {
    const hasOverride = (key: keyof ProductVariantOptionModel) =>
      Object.prototype.hasOwnProperty.call(overrides, key);

    return {
      name: option.name,
      description: option.description ?? null,
      product_variant_id: option.product_variant_id,
      price: option.price,
      category: option.category ?? 'Full Price',
      counts_toward_minimum_spend: option.counts_toward_minimum_spend ?? true,
      max_quantity: option.max_quantity ?? null,
      info_message: option.info_message ?? null,
      available_from: option.available_from ?? null,
      available_until: option.available_until ?? null,
      tier_complimentary_option_id: hasOverride('tier_complimentary_option_id')
        ? overrides.tier_complimentary_option_id ?? null
        : option.tier_complimentary_option_id ?? null,
      tier_half_option_id: hasOverride('tier_half_option_id')
        ? overrides.tier_half_option_id ?? null
        : option.tier_half_option_id ?? null,
      tier_full_limit: hasOverride('tier_full_limit')
        ? overrides.tier_full_limit ?? null
        : option.tier_full_limit ?? null,
      tier_complimentary_limit: hasOverride('tier_complimentary_limit')
        ? overrides.tier_complimentary_limit ?? null
        : option.tier_complimentary_limit ?? null,
    };
  }

  private toOptionalInt(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);

    return Number.isNaN(parsed) ? null : parsed;
  }

  private getTierOptionPrice(optionId: number | null | undefined): number | null {
    if (!optionId || !this.variant?.options) {
      return null;
    }

    return this.variant.options.find((option) => option.id === optionId)?.price ?? null;
  }

  private formatTierPrice(price: number | null | undefined): string {
    if (price === null || price === undefined) {
      return '£—';
    }

    return `£${price.toFixed(2)}`;
  }

  private syncTierControlAvailability() {
    const complimentaryControl = this.tierForm.controls.tier_complimentary_option_id;
    const halfControl = this.tierForm.controls.tier_half_option_id;

    if (this.tierComplimentaryOptions.length === 0) {
      complimentaryControl.disable({ emitEvent: false });
    } else {
      complimentaryControl.enable({ emitEvent: false });
    }

    if (this.tierHalfOptions.length === 0) {
      halfControl.disable({ emitEvent: false });
    } else {
      halfControl.enable({ emitEvent: false });
    }
  }
}
